<template>
  <entity-edit-dialog
      @submit="submit"
      @close="close"
      v-model="visible"
      ref="editDialog"
      :dialog-title="dialogTitle"
      :submitting="submitting">
    <template v-slot:contentCard>
      <v-card-text class="pt-5">
        <entity-dialog-input-row :label="$t('device.singular')" :required="true">
          <div class="d-flex flex-column">
            <device-select-field v-if="!useWriteInDeviceName"
                                 :show-add-dialog-button="true"
                                 :show-select-dialog-button="true"
                                 v-model="defectReport.device"
                                 :rules="[buildRequiredIfValidator(() => !useWriteInDeviceName),]"
                                 style="max-width: 30em"/>
            <v-text-field v-if="useWriteInDeviceName"
                          v-model="defectReport.writeInDeviceName"
                          outlined
                          hide-details="auto"
                          style="max-width: 30em"
                          :rules="[buildMaxLengthValidator(256), buildRequiredIfValidator(() => useWriteInDeviceName),]"/>
            <v-checkbox v-model="useWriteInDeviceName"
                        :label="$t('defectReport.useWriteInDeviceName')"
                        hide-details
                        dense
                        class="mt-0 pa-0"/>
          </div>
        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('defectReport.priority')" :required="true">
          <priority-select v-model="defectReport.priority"
                           :rules="[buildRequiredValidator(),]"
                           style="max-width: 13em"/>

        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('defectReport.title')" :required="true">
          <v-text-field v-model="defectReport.title"
                        outlined
                        hide-details="auto"
                        style="max-width: 20em"
                        autocomplete="false"
                        :rules="[buildMaxLengthValidator(256), buildRequiredValidator(),]"/>
        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('defectReport.description')" :required="true">
          <v-textarea
              v-model="defectReport.description"
              rows="3"
              outlined
              hide-details="auto"
              :rules="[buildRequiredValidator(),]"/>
        </entity-dialog-input-row>


        <entity-dialog-input-row v-if="defectReport.id"
                                 :label="$t('defectReport.status')"
                                 :required="!!defectReport.id">
          <defect-report-status-select v-model="defectReport.status"
                                       :rules="[buildRequiredIfValidator(() => !!defectReport.id),]"/>
        </entity-dialog-input-row>

        <entity-dialog-input-row :label="$t('defectReport.documents')">
          <documents-select v-model="documents"/>
        </entity-dialog-input-row>


        <entity-dialog-input-row :label="$t('defectReport.signer')" :required="isSignerRequired">
          <v-text-field v-model="defectReport.signer"
                        outlined
                        hide-details="auto"
                        style="max-width: 20em"
                        :rules="[buildMaxLengthValidator(512), buildRequiredIfValidator(()=> isSignerRequired),]"/>
        </entity-dialog-input-row>


        <entity-dialog-input-row divider-before>
          <v-checkbox :label="$t('defectReport.sendMails')" v-model="sendNotificationMails"/>
        </entity-dialog-input-row>


      </v-card-text>
    </template>
  </entity-edit-dialog>
</template>

<script>
import EntityDialogInputRow from '@/components/entity-crud/EntyDialogInputRow';
import DeviceSelectField from '@/components/inputs/DeviceSelectField';
import i18n from '@/i18n';
import PrioritySelect from '@/components/inputs/PrioritySelect';
import EntityEditDialog from '@/components/entity-crud/EntityEditDialog';
import DocumentsSelect from '@/components/inputs/DocumentsSelect';
import DefectReportsService from '@/services/DefectReportsService';
import DefectReportStatusSelect from '@/components/inputs/DefectReportStatusSelect';
import { buildRequiredIfValidator, buildRequiredValidator, buildMaxLengthValidator } from '@/utils/ValidatorBuilders';

export default {
  name: 'DefectReportDialog',
  components: {
    DefectReportStatusSelect,
    DocumentsSelect,
    EntityEditDialog,
    PrioritySelect,
    DeviceSelectField,
    EntityDialogInputRow,
  },
  data: () => ({
    sendNotificationMails: false,
    defectReport: null,
    documents: [],
    useWriteInDeviceName: false,
    visible: false,
    submitting: false,

  }),
  props: {},
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    dialogTitle() {
      if (this.defectReport?.id) {
        return i18n.t('defectReport.editDialog.titleEdit');
      } else {
        return i18n.t('defectReport.editDialog.titleAdd');
      }
    },
    isSignerRequired() {
      return this.$store.getters['userSessionStateStore/user']?.sharedAccount;
    },
  },
  methods: {
    buildRequiredIfValidator,
    buildRequiredValidator,
    buildMaxLengthValidator,
    close() {
      this.defectReport = null;
      this.visible = false;
      this.submitting = false;
    },

    openCreateNewDefectReport() {
      this.defectReport = {
        name: null,
        priority: null,
        status: 'NEW',
        title: null,
        description: null,
        signer: null,
        writeInDeviceName: null,


      };
      this.documents = [];
      this.visible = true;
    },

    openEditForDefectReport(defectReportId) {
      DefectReportsService.get(defectReportId)
          .then(result => {
            this.defectReport = result.data.defectReport;
            this.documents = result.data.documents;

            this.useWriteInDeviceName = !result.data.defectReport.device;

            this.visible = true;
          }).catch(err => {
        console.log(JSON.stringify(err));
      });
    },


    submit() {
      if (this.submitting) {
        return;
      }


      if (this.useWriteInDeviceName) {
        this.defectReport.device = null;
      } else {
        this.defectReport.writeInDeviceName = null;
      }

      if (!this.$refs.editDialog.$refs.form.validate()) {
        this.$refs.editDialog.showFormHasValidationErrorsMessage(i18n.t('defectReport.requiredInformationMissing'));
        return;
      }


      let defectReportDTO = {
        defectReport: this.defectReport,
        documents: this.documents,
        sendNotificationMails: this.sendNotificationMails,
      };

      this.submitting = true;
      DefectReportsService.save(defectReportDTO).then((result) => {
        this.$emit('done', result.data);
        this.close();
      }).catch(err => {
        console.log(JSON.stringify(err));
      }).finally(() => {
        this.submitting = false;
      });
    },
  },
};
</script>

<style scoped>
.repeat-weekdays > * {
  margin-right: 1em;
}
</style>
